<template>
  <!-- Overlay (solo mobile) -->
  <transition name="fade">
    <div
      v-if="isOpen"
      class="sidebar-overlay d-lg-none"
      @click="$emit('close')"
    />
  </transition>

  <!-- Sidebar -->
  <div
    class="sidebar d-flex flex-column vh-100 sidebar-ultra-blue p-3 border-end border-info border-opacity-20 shadow-lg"
    :class="{ 'sidebar-open': isOpen }"
    style="z-index: 1060;"
  >
    <router-link to="/" class="d-flex align-items-start mb-4 px-2 mt-2 text-decoration-none logo-link" @click="handleNavClick">
      <div class="d-flex align-items-center justify-content-start w-100">
        <img src="../assets/logo4.png" alt="Logo OT" class="corporate-logo-mini" />
        <h5 class="px-2 text-white fw-bold">Maki OT</h5>
      </div>
    </router-link>

    <!-- Menú Principal -->
    <ul class="nav nav-pills flex-column mb-auto px-1 custom-scrollbar overflow-auto">
      <li class="nav-item mb-1" v-for="item in menuItems" :key="item.label">
        <router-link
          class="nav-link d-flex align-items-center rounded-3 px-3 py-2 position-relative"
          :to="item.path"
          active-class="active shadow-neon"
          @click="handleNavClick"
        >
          <i :class="'bi ' + item.icon + ' me-3 fs-5'"></i>
          <span class="text-truncate">{{ item.label }}</span>
          <div class="active-indicator" v-if="$route.path === item.path"></div>
        </router-link>
      </li>
    </ul>

    <!-- Contactos de Soporte (Premium Card) -->
    <div class="px-2 mb-3 mt-4">
      <div class="support-card rounded-4 p-3 border border-white border-opacity-10 shadow-sm overflow-hidden">
        <div class="d-flex align-items-center mb-2">
          <div class="bg-success-neon me-2 rounded-circle d-flex align-items-center justify-content-center" style="width: 8px; height: 8px;"></div>
          <span class="small fw-bold text-white opacity-70 text-uppercase tracking-wider" style="font-size: 0.65rem;">Central de Soporte</span>
        </div>
        
        <div class="contact-item mb-2">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="small text-info opacity-75 fw-medium">Técnicos</span>
            <i class="bi bi-whatsapp text-success opacity-75 small"></i>
          </div>
          <a href="https://wa.me/56996589680" target="_blank" class="fw-bold text-white small text-decoration-none d-block">+569 9658 9680</a>
        </div>

        <div class="contact-item">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="small text-info opacity-75 fw-medium">Informáticos</span>
            <i class="bi bi-whatsapp text-success opacity-75 small"></i>
          </div>
          <a href="https://wa.me/56998444698" target="_blank" class="fw-bold text-white small text-decoration-none d-block">+569 9844 4698</a>
        </div>
        
        <div class="support-bg-blur"></div>
      </div>
    </div>

    <!-- Menú de Usuario (Ultra Blue Card Style) -->
    <div class="px-2 pb-3 pt-3 border-top border-info border-opacity-20 mt-auto position-relative">
      <div class="dropup">
        <button 
          class="btn btn-user-blue w-100 d-flex align-items-center justify-content-between p-2 rounded-3 border border-info border-opacity-20 shadow-sm" 
          type="button" 
          @click.stop="toggleUserMenu"
          :class="{ 'show': isMenuOpen }"
        >
          <div class="d-flex align-items-center overflow-hidden">
            <div class="bg-electric-subtle rounded-circle p-1 me-2 d-flex align-items-center justify-content-center flex-shrink-0" style="width: 32px; height: 32px;">
              <i class="bi bi-person-fill text-white"></i>
            </div>
            <span class="text-truncate small fw-bold text-white opacity-90">{{ session.user?.nombre || 'Usuario' }}</span>
          </div>
          <i class="bi bi-caret-up-fill small ms-1 text-white opacity-50 transition-icon" :class="{ 'rotate-180': isMenuOpen }"></i>
        </button>
        
        <!-- Menú Desplegable Premium Blur -->
        <ul class="dropdown-menu dropdown-ultra-blue shadow-lg w-100 border border-info border-opacity-20 mb-2 position-absolute" 
            :class="{ 'show': isMenuOpen }"
            style="bottom: 100%; left: 0; display: block; visibility: hidden; opacity: 0; transform: translateY(10px); transition: all 0.2s ease;"
            :style="isMenuOpen ? 'visibility: visible; opacity: 1; transform: translateY(0); pointer-events: auto;' : 'pointer-events: none;'"
        >
          <li class="px-2 pt-2 pb-1">
            <div class="small fw-bold text-info opacity-50 text-uppercase mb-1 px-3" style="font-size: 0.65rem; letter-spacing: 0.5px;">Ajustes</div>
          </li>
          <li>
            <router-link class="dropdown-item d-flex align-items-center py-2 rounded-2 mx-2 w-auto" to="/perfil" @click="handleNavClick">
              <i class="bi bi-person-badge me-2 text-electric"></i> 
              <span class="text-white opacity-80">Mi Perfil</span>
            </router-link>
          </li>
          <li>
            <hr class="dropdown-divider mx-3 my-2 border-white border-opacity-10" />
          </li>
          <li>
            <a class="dropdown-item d-flex align-items-center py-2 rounded-2 mx-2 w-auto text-danger-neon fw-bold" @click="logout">
              <i class="bi bi-box-arrow-right me-2"></i> 
              <span>Cerrar Sesión</span>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Theme Toggle Premium -->
    <div class="px-2 mt-2">
      <button 
        @click="themeStore.toggleTheme" 
        class="btn btn-theme-toggle w-100 d-flex align-items-center justify-content-center p-2 rounded-3 border shadow-sm transition-all"
      >
        <i :class="themeStore.theme === 'light' ? 'bi-sun-fill text-warning' : 'bi-moon-stars-fill text-info'" class="bi me-2 fs-5"></i>
        <span class="small fw-bold text-white opacity-90">Tema: {{ themeStore.theme === 'light' ? 'Claro' : 'Oscuro' }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '../stores/session'
import { useThemeStore } from '../stores/theme'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const router = useRouter()
const session = useSessionStore()
const themeStore = useThemeStore()
const isMenuOpen = ref(false)

const toggleUserMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

// Cierra el drawer y el menú al navegar
const handleNavClick = () => {
  isMenuOpen.value = false
  emit('close')
}

const logout = () => {
  isMenuOpen.value = false
  session.logout()
  router.push('/login')
  emit('close')
}

// Cerrar menú al hacer click fuera
onMounted(() => {
  window.addEventListener('click', closeMenu)
})

onUnmounted(() => {
  window.removeEventListener('click', closeMenu)
})

const menuItems = computed(() => {
  const items = [
    { label: 'Incidencias Activas', path: '/ot', icon: 'bi-list-ul' },
    { label: 'Historial OT', path: '/ot/buscar', icon: 'bi-clock-history' },
    { label: 'Ver Máquinas', path: '/maquinas', icon: 'bi-bus-front' }
  ]

  if (['admin', 'tecnico'].includes(session.user?.rol)) {
    items.push({ label: 'Rastrear Máquina', path: '/rastrear', icon: 'bi-geo-alt' })
  }

  if (session.user?.rol === 'admin') {
    items.push(
      { label: 'Administrar Sistema',   path: '/admin',    icon: 'bi-gear' },
      { label: 'Administrar Usuarios',  path: '/usuarios', icon: 'bi-people' }
    )
  }

  return items
})
</script>

<style scoped>
.fw-extrabold { font-weight: 800; }
.tracking-tight { letter-spacing: -0.025em; }

.corporate-logo-mini {
  height: 55px;
  width: auto;
  object-fit: contain;
}

/* ===================== SIDEBAR DESKTOP ===================== */
.sidebar {
  width: 260px;
  min-width: 260px;
  max-width: 260px;
  flex-shrink: 0;
}

/* ===================== SIDEBAR MOBILE (Drawer) ===================== */
@media (max-width: 991px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100dvh;
    transform: translateX(-100%);
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 1060;
    width: 280px;
    min-width: 280px;
    max-width: 280px;
  }

  .sidebar.sidebar-open {
    transform: translateX(0);
  }
}

/* ===================== OVERLAY ===================== */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1059;
  backdrop-filter: blur(2px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===================== ESTILOS EXISTENTES ===================== */
.sidebar-ultra-blue {
  background: var(--bg-sidebar) !important;
  /* backdrop-filter eliminado — muy costoso para GPU (fuerza composite en cada frame) */
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.3);
  will-change: transform;
  transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1);
}

.bg-electric { background: var(--accent-gradient); }
.bg-electric-subtle { background-color: rgba(14, 165, 233, 0.2); }
.text-electric { color: var(--accent-primary); }
.text-danger-neon { color: #fb7185; }
.bg-success-neon { background-color: #4ade80; box-shadow: 0 0 10px #4ade80; }
.tracking-wider { letter-spacing: 0.05em; }

.shadow-neon {
  box-shadow: var(--shadow-neon);
}

.nav-link {
  color: var(--text-secondary) !important;
  /* Solo anima propiedades compositeadas (sin layout triggers) */
  transition: background-color 0.2s ease, color 0.2s ease;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid transparent;
}

.nav-link:hover {
  color: var(--text-primary) !important;
  background-color: var(--bg-card-hover);
}

.nav-link.active {
  background: var(--accent-gradient) !important;
  color: #ffffff !important;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.4);
}

.active-indicator {
  position: absolute;
  left: 0;
  height: 60%;
  width: 4px;
  background: #00f2ff;
  border-radius: 0 4px 4px 0;
  box-shadow: 0 0 15px #00f2ff;
}

.btn-user-blue {
  background: rgba(14, 165, 233, 0.1);
  border: 1px solid var(--border-glass) !important;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  will-change: background-color;
}

.btn-user-blue:hover {
  background: rgba(14, 165, 233, 0.2);
  border-color: rgba(14, 165, 233, 0.3) !important;
}

.btn-theme-toggle {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-glass) !important;
  transition: all 0.3s ease;
}

.btn-theme-toggle:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.dropdown-ultra-blue {
  background: var(--bg-sidebar) !important;
  /* backdrop-filter eliminado — costoso en dropdowns que se abren/cierran frecuentemente */
  border: 1px solid var(--border-glass) !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.dropdown-item {
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.transition-icon {
  transition: transform 0.3s ease;
}

.rotate-180 {
  transform: rotate(180deg);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.support-card {
  background: rgba(255, 255, 255, 0.03);
  position: relative;
  transition: background-color 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  will-change: transform, background-color;
}

.support-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(14, 165, 233, 0.3) !important;
}

.support-bg-blur {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, rgba(14, 165, 233, 0) 70%);
  filter: blur(10px);
  z-index: -1;
}

.contact-item {
  position: relative;
  z-index: 1;
}
</style>
